import React from "react";

// App
import { ApiPath, Field, Notice, Section } from "./apiField";

// Both blocks read the same `hotelOTA` document, so an absent block has two
// quite different causes - no document at all, or a document that simply does
// not carry this block - and they are reported apart rather than as one
// "missing" state.
function NoDocument({ source }) {
  return (
    <Notice tone="warn">
      No <code>hotelOTA</code> document for this hotel
      {source ? (
        <>
          {" "}
          (<code>sources.ota: {source}</code>)
        </>
      ) : null}
      .
    </Notice>
  );
}

function NoBlock({ block }) {
  return (
    <Notice tone="info">
      This hotel has a <code>hotelOTA</code> document, but it carries no{" "}
      <code>{block}</code> block.
    </Notice>
  );
}

const hotelsUrl = (path) => `https://www.radissonhotels.com${path}`;

export function ArrivalSection({ arrival, source, raw }) {
  return (
    <Section
      id="arrival"
      title="Getting here"
      subtitle={arrival.title}
      path="ota.collection[0].content.howToArrive"
      raw={raw}
    >
      {!arrival.hasDoc ? <NoDocument source={source} /> : null}
      {arrival.hasDoc && !arrival.hasEntry ? (
        <NoBlock block="howToArrive" />
      ) : null}

      {arrival.paragraphs.length ? (
        <div className="rhp-prose">
          {arrival.paragraphs.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
          <ApiPath path={arrival.paragraphsPath} />
        </div>
      ) : null}

      {arrival.groups.length ? (
        <div className="rhp-arrival">
          {arrival.groups.map((group) => (
            <article className="rhp-arrival__group" key={group.index}>
              <h4>
                {group.title}
                {group.mode ? (
                  <span className="rhp-badge">{group.mode}</span>
                ) : null}
              </h4>

              {group.legs.map((leg) => (
                <div className="rhp-arrival__leg" key={leg.index}>
                  <span className="rhp-arrival__mode">{leg.label}</span>
                  <div>
                    {leg.paragraphs.map((text, i) => (
                      <p key={i}>{text}</p>
                    ))}
                  </div>
                </div>
              ))}

              <ApiPath path={group.path} />
            </article>
          ))}
        </div>
      ) : null}

      {arrival.hasEntry && !arrival.groups.length ? (
        <Notice tone="warn">
          The document carries <code>howToArrive</code> but no origin groups.
        </Notice>
      ) : null}

      {arrival.hasEntry ? (
        <div className="rhp-grid rhp-grid--tight">
          <Field
            label="Hotel page"
            value={arrival.hotelPage}
            path={arrival.hotelPagePath}
          >
            {arrival.hotelPage ? (
              <a
                href={hotelsUrl(arrival.hotelPage)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {arrival.hotelPage}
              </a>
            ) : null}
          </Field>
          <Field
            label="Contact page"
            value={arrival.contactPage}
            path={arrival.contactPagePath}
          >
            {arrival.contactPage ? (
              <a
                href={hotelsUrl(arrival.contactPage)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {arrival.contactLabel || arrival.contactPage}
              </a>
            ) : null}
          </Field>
        </div>
      ) : null}
    </Section>
  );
}

export function WellnessSection({ fitness, source, raw }) {
  return (
    <Section
      id="wellness"
      title="Fitness & wellness"
      subtitle={fitness.title}
      path="ota.collection[0].content.fitness"
      raw={raw}
    >
      {!fitness.hasDoc ? <NoDocument source={source} /> : null}
      {fitness.hasDoc && !fitness.hasEntry ? <NoBlock block="fitness" /> : null}

      {fitness.hasEntry ? (
        <div className="rhp-intro">
          <div>
            {fitness.title ? <h3>{fitness.title}</h3> : null}
            {fitness.paragraphs.map((text, i) => (
              <p key={i}>{text}</p>
            ))}
            <ApiPath path={fitness.paragraphsPath} />
          </div>
        </div>
      ) : null}

      {fitness.hasEntry && !fitness.paragraphs.length ? (
        <Notice tone="warn">
          <code>fitness</code> is published with no description.
        </Notice>
      ) : null}
    </Section>
  );
}
